import type {
  RecordedChunk,
  RecorderAdapter,
  RecorderCallbacks,
  RecorderState,
} from '../../visual/index.js';

/**
 * `MediaRecorder`, behind the adapter the session holds.
 *
 * The same arrangement the encoders use: everything browser-specific is in this
 * file, the session sees five methods and a state, and a test can supply a fake
 * that stops when told and never delivers a chunk. That matters more here than
 * usual, because the interesting behaviour of a recorder — the final chunk
 * arriving after `stop()` — is precisely what cannot be exercised without
 * controlling it.
 *
 * ## Blobs stay here
 *
 * The session counts chunks and adds up sizes; it never holds a `Blob`. The
 * pieces are kept in this file and assembled by `toBlob` at the end, so the
 * platform-free layer stays free of platform types and the concatenation
 * happens once rather than on every chunk.
 */

interface MediaRecorderLike {
  readonly state: RecorderState;
  readonly mimeType: string;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
  start(timeslice?: number): void;
  stop(): void;
}

interface MediaRecorderConstructor {
  new (stream: MediaStream, options?: { mimeType?: string; videoBitsPerSecond?: number }): MediaRecorderLike;
  isTypeSupported(mime: string): boolean;
}

function recorderGlobal(): MediaRecorderConstructor | undefined {
  return (globalThis as unknown as { MediaRecorder?: MediaRecorderConstructor }).MediaRecorder;
}

/** Whether this browser can record at all. */
export function mediaRecorderAvailable(): boolean {
  return typeof recorderGlobal() === 'function';
}

/** Asks the browser whether it can record a given container and codec pair. */
export function probeRecordingMime(mime: string): boolean {
  const Recorder = recorderGlobal();
  if (Recorder === undefined) return false;
  try {
    return Recorder.isTypeSupported(mime);
  } catch {
    return false;
  }
}

export interface MediaRecorderAdapterOptions {
  readonly stream: MediaStream;
  readonly mimeType: string;
  readonly videoBitsPerSecond?: number;
  readonly callbacks: RecorderCallbacks;
}

export class MediaRecorderAdapter implements RecorderAdapter {
  private recorder: MediaRecorderLike | null = null;
  private readonly parts: Blob[] = [];
  private failed = false;

  constructor(private readonly options: MediaRecorderAdapterOptions) {
    const Recorder = recorderGlobal();
    if (Recorder === undefined) throw new Error('MediaRecorder is not available in this browser');

    this.recorder = new Recorder(options.stream, {
      mimeType: options.mimeType,
      ...(options.videoBitsPerSecond === undefined
        ? {}
        : { videoBitsPerSecond: options.videoBitsPerSecond }),
    });

    this.recorder.ondataavailable = (event): void => {
      const data = event.data;
      if (data === undefined || data.size === 0) return;
      this.parts.push(data);
      const chunk: RecordedChunk = { size: data.size };
      options.callbacks.onChunk(chunk);
    };
    // Fires after the last `dataavailable`, which is the whole reason the
    // session has a STOPPING state: this is the moment a recording is finished,
    // and it is later than the moment it was asked to stop.
    this.recorder.onstop = (): void => {
      options.callbacks.onStop();
    };
    this.recorder.onerror = (event): void => {
      this.failed = true;
      const error = (event as { error?: { message?: string } } | undefined)?.error;
      options.callbacks.onError(error?.message ?? 'recorder error');
    };
  }

  get state(): RecorderState {
    return this.recorder?.state ?? 'inactive';
  }

  get mimeType(): string {
    // The browser's answer, not the requested string — they differ often enough
    // that reporting the request would mislabel the file.
    return this.recorder?.mimeType ?? this.options.mimeType;
  }

  start(timesliceMs?: number): void {
    const recorder = this.recorder;
    if (recorder === null) throw new Error('recorder was disposed');
    if (recorder.state !== 'inactive') return;
    this.parts.length = 0;
    if (timesliceMs === undefined) recorder.start();
    else recorder.start(timesliceMs);
  }

  stop(): void {
    const recorder = this.recorder;
    if (recorder === null || recorder.state === 'inactive') return;
    recorder.stop();
  }

  /** The finished recording, once the session says it is done. */
  toBlob(): Blob | null {
    if (this.parts.length === 0) return null;
    return new Blob(this.parts, { type: this.mimeType });
  }

  dispose(): void {
    const recorder = this.recorder;
    if (recorder !== null) {
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        // Already stopped, or stopped by the error path.
      }
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
    }
    this.recorder = null;
    this.parts.length = 0;
  }

  diagnostics(): {
    readonly state: RecorderState;
    readonly mimeType: string;
    readonly parts: number;
    readonly bytes: number;
    readonly failed: boolean;
  } {
    return {
      state: this.state,
      mimeType: this.mimeType,
      parts: this.parts.length,
      bytes: this.parts.reduce((total, part) => total + part.size, 0),
      failed: this.failed,
    };
  }
}
